import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import {
  dataRequestActionApproveMutation,
  dataRequestListOptions,
  dataRequestListQueryKey,
  dataRequestActionRejectMutation,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type { DataRequestBean } from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import { ConfirmModal } from '../components/ConfirmModal';

type DataRequestType = NonNullable<DataRequestBean['type']>;
type DataRequestActionType = 'approve' | 'reject';

const DATA_REQUEST_TYPES: ReadonlyArray<DataRequestType> = ['HOST_COMPANY'];

type PendingAction = {
  actionType: DataRequestActionType;
  request: DataRequestBean;
};

function formatTimestamp(timestamp: string | undefined): string {
  if (!timestamp) {
    return '-';
  }

  const normalizedTimestamp = timestamp.includes('T')
    ? timestamp.endsWith('Z')
      ? timestamp
      : `${timestamp}Z`
    : `${timestamp.replace(' ', 'T')}Z`;
  const parsedDate = new Date(normalizedTimestamp);

  if (Number.isNaN(parsedDate.getTime())) {
    return timestamp;
  }

  return parsedDate.toLocaleString();
}

function getContentJson(request: DataRequestBean): string {
  const content = Object.entries(request).reduce<Record<string, unknown>>((accumulator, entry) => {
    const [key, value] = entry;

    if (key === 'auditBean' || key === 'id' || value === undefined) {
      return accumulator;
    }

    accumulator[key] = value;
    return accumulator;
  }, {});

  return JSON.stringify(content, null, 2);
}

export function DataRequest(): ReactElement {
  const [selectedType, setSelectedType] = useState<DataRequestType>('HOST_COMPANY');
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [modalErrorMessage, setModalErrorMessage] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveSelection, setApproveSelection] = useState('');

  const listQuery = useQuery(
    dataRequestListOptions({
      query: {
        type: selectedType,
      },
    })
  );

  const approveMutation = useMutation(dataRequestActionApproveMutation());
  const rejectMutation = useMutation(dataRequestActionRejectMutation());

  const isSubmitting = approveMutation.isPending || rejectMutation.isPending;

  const rows = useMemo(() => {
    return [...(listQuery.data?.results ?? [])];
  }, [listQuery.data]);

  function closeModal(): void {
    if (isSubmitting) {
      return;
    }

    setPendingAction(null);
    setModalErrorMessage(null);
    setRejectReason('');
    setApproveSelection('');
  }

  async function refreshList(): Promise<void> {
    await queryClient.invalidateQueries({
      queryKey: dataRequestListQueryKey({
        query: {
          type: selectedType,
        },
      }),
    });
  }

  async function handleConfirmAction(): Promise<void> {
    if (!pendingAction?.request.id) {
      setModalErrorMessage('Cannot submit action because the request id is missing.');
      return;
    }

    setModalErrorMessage(null);

    try {
      if (pendingAction.actionType === 'approve') {
        const trimmedApproveSelection = approveSelection.trim();

        await approveMutation.mutateAsync({
          path: { id: pendingAction.request.id },
          query: {
            ...(trimmedApproveSelection.length > 0 ? { selection: trimmedApproveSelection } : {}),
          },
        });
      } else {
        const trimmedRejectReason = rejectReason.trim();

        if (!trimmedRejectReason) {
          setModalErrorMessage('Please enter a reason before rejecting.');
          return;
        }

        await rejectMutation.mutateAsync({
          path: { id: pendingAction.request.id },
          query: {
            reason: trimmedRejectReason,
          },
        });
      }

      await refreshList();
      closeModal();
    } catch (error) {
      const fallbackMessage =
        pendingAction.actionType === 'approve'
          ? 'Unable to approve request.'
          : 'Unable to reject request.';
      setModalErrorMessage(getApiErrorMessage(error, fallbackMessage));
    }
  }

  const queryErrorMessage = listQuery.error
    ? getApiErrorMessage(listQuery.error, 'Unable to load data requests.')
    : null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Data Requests</h1>
      <p className="mt-2 text-sm text-slate-600">
        Review pending data requests and approve or reject them.
      </p>

      <div className="mt-6 max-w-xs">
        <label
          className="grid gap-1 text-sm font-medium text-slate-700"
          htmlFor="data-request-type"
        >
          Data request type
          <select
            id="data-request-type"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-sky-500 focus:ring-2"
            value={selectedType}
            onChange={(event) => setSelectedType(event.target.value as DataRequestType)}
          >
            {DATA_REQUEST_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-6 overflow-hidden rounded-md border border-slate-200">
        {listQuery.isPending ? (
          <p className="px-3 py-2 text-sm text-slate-600">Loading data requests...</p>
        ) : queryErrorMessage ? (
          <p className="px-3 py-2 text-sm text-rose-700">{queryErrorMessage}</p>
        ) : rows.length === 0 ? (
          <p className="px-3 py-2 text-sm text-slate-600">No requests found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Id
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Date
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Content
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((request, index) => {
                  const id = request.id ?? `unknown-${index}`;
                  const isRowActionDisabled = !request.id || isSubmitting;

                  return (
                    <tr key={id}>
                      <td className="whitespace-nowrap px-3 py-2 align-top font-mono text-xs text-slate-800">
                        {request.id ?? '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-top text-xs text-slate-700">
                        {formatTimestamp(request.auditBean?.timestamp)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md bg-slate-50 p-2 text-xs text-slate-800">
                          {getContentJson(request)}
                        </pre>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isRowActionDisabled}
                            onClick={() => {
                              setModalErrorMessage(null);
                              setRejectReason('');
                              setApproveSelection('');
                              setPendingAction({
                                actionType: 'approve',
                                request,
                              });
                            }}
                            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={isRowActionDisabled}
                            onClick={() => {
                              setModalErrorMessage(null);
                              setRejectReason('');
                              setApproveSelection('');
                              setPendingAction({
                                actionType: 'reject',
                                request,
                              });
                            }}
                            className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={pendingAction !== null}
        onClose={closeModal}
        onSubmit={() => {
          void handleConfirmAction();
        }}
        title={
          pendingAction?.actionType === 'approve' ? 'Approve data request?' : 'Reject data request?'
        }
        message={
          pendingAction?.actionType === 'approve'
            ? `Approve request ${pendingAction.request.id ?? 'unknown'}?`
            : `Reject request ${pendingAction?.request.id ?? 'unknown'}?`
        }
        ariaLabel={
          pendingAction?.actionType === 'approve'
            ? 'Approve data request confirmation'
            : 'Reject data request confirmation'
        }
        isSubmitting={isSubmitting}
        isSubmitDisabled={
          isSubmitting ||
          pendingAction === null ||
          (pendingAction.actionType === 'reject' && rejectReason.trim().length === 0)
        }
        submitLabel={pendingAction?.actionType === 'approve' ? 'Approve' : 'Reject'}
        submittingLabel={pendingAction?.actionType === 'approve' ? 'Approving...' : 'Rejecting...'}
        errorMessage={modalErrorMessage}
      >
        {pendingAction?.actionType === 'reject' ? (
          <label className="block text-sm text-slate-700" htmlFor="reject-reason-input">
            Reject reason
            <textarea
              id="reject-reason-input"
              value={rejectReason}
              onChange={(event) => {
                setRejectReason(event.target.value);
                if (modalErrorMessage) {
                  setModalErrorMessage(null);
                }
              }}
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              placeholder="Enter reason"
            />
          </label>
        ) : pendingAction?.actionType === 'approve' ? (
          <label className="block text-sm text-slate-700" htmlFor="approve-selection-input">
            Selection (optional)
            <input
              id="approve-selection-input"
              type="text"
              value={approveSelection}
              onChange={(event) => {
                setApproveSelection(event.target.value);
                if (modalErrorMessage) {
                  setModalErrorMessage(null);
                }
              }}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              placeholder="Enter selection"
            />
          </label>
        ) : null}
      </ConfirmModal>
    </section>
  );
}
